import { useState, useEffect, useMemo } from 'react';
import { spinnerRing } from '../utils/bookingUtils';
import { subscribePosAudit, filterPosAuditEntries } from '../utils/posUtil';
import { POS_AUDIT_LABELS, POS_AUDIT_TONES, POS_PAGE_SIZE } from '../constants/posConst';
import PosPager from './PosPager';
import PosAuditDetailModal from './PosAuditDetailModal';
import '../css/Pos.css';

/**
 * Renders one entry's summary without needing to know every action's shape.
 *
 * The summaries are small, deliberately flat objects (see posUtil), so this
 * lists their fields rather than switching on action type — a new POS action
 * then shows up here correctly without this component being touched.
 *
 * @param {{summary: object|null}} props
 * @returns {JSX.Element|null}
 */
const AuditSummary = ({ summary }) => {
  if (!summary || typeof summary !== 'object') return null;
  const entries = Object.entries(summary).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return null;

  return (
    <dl className="pos-audit-summary">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>
            {Array.isArray(value)
              ? value.join(', ')
              : (typeof value === 'object'
                ? Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(', ')
                : String(value))}
          </dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * POS audit trail — product registrations, product changes and orders.
 *
 * Deliberately separate from the booking app's /auditLog: a counter sale and
 * a court reservation are different things to review, and the booking trail
 * is busy enough that POS entries would be lost in it.
 *
 * @returns {JSX.Element}
 */
function PosAuditTrail() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  // The three filters compose, so they are held together and cleared
  // together. Dates start empty rather than at today: an audit trail is
  // consulted to find something that already happened, and opening it
  // pre-narrowed to one day would hide most of what is there.
  const [filters, setFilters] = useState({ action: '', search: '', startDate: '', endDate: '' });
  const hasFilters = Object.values(filters).some(Boolean);

  useEffect(() => {
    const stop = subscribePosAudit((list) => { setEntries(list); setLoading(false); });
    return () => stop();
  }, []);

  const visible = useMemo(() => filterPosAuditEntries(entries, filters), [entries, filters]);

  // The trail is a LIVE subscription, so the list can shrink under the reader
  // (an admin narrows the filter, or an older entry ages out) and leave the
  // page number pointing past the end — which renders an empty section with
  // no explanation. Pulling it back to the last real page keeps rows on
  // screen whatever the list does.
  const pageCount = Math.max(1, Math.ceil(visible.length / POS_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = visible.slice((safePage - 1) * POS_PAGE_SIZE, safePage * POS_PAGE_SIZE);

  /**
   * Changes one filter and returns to the first page — the page the reader
   * was on rarely exists in the newly filtered list, and landing on an empty
   * page reads as "no results" when there are plenty.
   *
   * @param {string} key - 'action' | 'search' | 'startDate' | 'endDate'
   * @param {string} value - the new value for that filter
   * @returns {void}
   */
  const handleFilterChange = (key, value) => {
    setFilters(current => ({ ...current, [key]: value }));
    setPage(1);
  };

  /**
   * Clears every filter at once and returns to the first page.
   * @returns {void}
   */
  const handleClearFilters = () => {
    setFilters({ action: '', search: '', startDate: '', endDate: '' });
    setPage(1);
  };

  if (loading) return <div className="pos-loading">{spinnerRing('Loading audit trail…')}</div>;

  return (
    <div className="pos-audit">
      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Audit Trail</h2>
          <p>Product registrations, changes and orders — POS only. Tap a record to see its full details.</p>
        </div>

        <div className="pos-audit-filters">
          {/* Searches the entry's details, not just its title — the product
              names, categories and prices inside the summary — because
              "when did we last sell Chippy" is the question this trail is
              actually opened for. */}
          <label className="pos-field pos-audit-search">
            <span>Search</span>
            <input
              type="search"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Product, category, staff…"
            />
          </label>

          <label className="pos-field">
            <span>Action</span>
            <select
              className="pos-audit-filter"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            >
              <option value="">All actions</option>
              {Object.entries(POS_AUDIT_LABELS).map(([action, label]) => (
                <option key={action} value={action}>{label}</option>
              ))}
            </select>
          </label>

          <label className="pos-field">
            <span>From</span>
            <input
              type="date"
              value={filters.startDate}
              max={filters.endDate || undefined}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </label>

          <label className="pos-field">
            <span>To</span>
            <input
              type="date"
              value={filters.endDate}
              min={filters.startDate || undefined}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </label>

          {/* Only offered once there is something to clear, so the bar stays
              quiet on arrival. */}
          {hasFilters && (
            <button type="button" className="pos-btn pos-btn-ghost pos-audit-clear" onClick={handleClearFilters}>
              Clear
            </button>
          )}
        </div>

        {visible.length === 0 ? (
          <p className="pos-empty-line">
            {hasFilters ? 'No entries match these filters.' : 'Nothing recorded yet.'}
          </p>
        ) : (
          <ul className="pos-audit-list">
            {pageRows.map((entry) => (
              // A button role rather than a real <button>: the row holds a
              // <dl>, which is not valid inside one. Enter and Space are
              // wired by hand for the same reason.
              <li
                key={entry.id}
                className={`pos-audit-row tone-${POS_AUDIT_TONES[entry.action] || 'info'}`}
                role="button"
                tabIndex={0}
                onClick={() => setViewing(entry)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewing(entry); }
                }}
              >
                <div className="pos-audit-top">
                  <span className="pos-audit-action">{POS_AUDIT_LABELS[entry.action] || entry.action}</span>
                  <span className="pos-audit-when">
                    {new Date(entry.timestamp).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
                <div className="pos-audit-by">{entry.performedBy}</div>
                <AuditSummary summary={entry.summary} />
              </li>
            ))}
          </ul>
        )}

        <PosPager page={safePage} totalItems={visible.length} onPageChange={setPage} />
      </section>

      <PosAuditDetailModal entry={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

export default PosAuditTrail;
